import { parse } from 'acorn'

export interface RuntimeDiagnostic {
  severity: 'error' | 'warning'
  code: string
  message: string
  line?: number
  column?: number
  path?: string
}

class RuntimeFailure extends Error {
  line?: number
  column?: number

  constructor(public code: string, message: string, node?: any) {
    super(message)
    this.line = node?.loc?.start?.line
    this.column = node?.loc?.start?.column !== undefined ? node.loc.start.column + 1 : undefined
  }
}

class ReturnSignal {
  constructor(public value: unknown) {}
}

class Environment {
  private values = new Map<string, unknown>()
  constructor(private parent?: Environment) {}

  declare(name: string, value: unknown) { this.values.set(name, value) }
  get(name: string, node?: any): unknown {
    if (this.values.has(name)) return this.values.get(name)
    if (this.parent) return this.parent.get(name, node)
    throw new RuntimeFailure('unknown-identifier', `Unknown identifier “${name}”.`, node)
  }
  set(name: string, value: unknown, node?: any): unknown {
    if (this.values.has(name)) {
      this.values.set(name, value)
      return value
    }
    if (this.parent) return this.parent.set(name, value, node)
    throw new RuntimeFailure('unknown-identifier', `Cannot assign undeclared identifier “${name}”.`, node)
  }
}

const FORBIDDEN_PROPERTIES = new Set(['constructor', '__proto__', 'prototype', 'caller', 'callee', 'arguments'])
const ARRAY_METHODS = new Set(['push', 'pop', 'shift', 'unshift', 'slice', 'map', 'filter', 'reduce', 'forEach', 'find', 'findIndex', 'includes', 'indexOf', 'join'])
const STRING_METHODS = new Set(['slice', 'substring', 'toLowerCase', 'toUpperCase', 'includes', 'startsWith', 'endsWith', 'trim'])
const MAX_SOURCE_LENGTH = 50_000
const MAX_STEPS = 50_000
const MAX_CALL_DEPTH = 64

function safeMath() {
  const result = Object.create(null) as Record<string, unknown>
  for (const key of ['abs', 'acos', 'asin', 'atan', 'atan2', 'ceil', 'cos', 'exp', 'floor', 'hypot', 'log', 'log10', 'max', 'min', 'pow', 'round', 'sign', 'sin', 'sqrt', 'tan', 'trunc']) {
    result[key] = (Math as any)[key].bind(Math)
  }
  result.PI = Math.PI
  result.E = Math.E
  result.LN2 = Math.LN2
  result.LN10 = Math.LN10
  return Object.freeze(result)
}

function safeArray() {
  const result = Object.create(null) as Record<string, unknown>
  result.from = (value: unknown, mapper?: (...args: unknown[]) => unknown) => {
    const requestedLength = Number((value as any)?.length ?? 0)
    if (!Number.isFinite(requestedLength) || requestedLength < 0 || requestedLength > 256) {
      throw new Error('Array.from() is limited to 256 items in recipe programs.')
    }
    const array = Array.from(value as ArrayLike<unknown>)
    return mapper ? array.map((item, index) => mapper(item, index)) : array
  }
  result.isArray = Array.isArray
  return Object.freeze(result)
}

export function executeRecipeProgram(source: string, globals: Record<string, unknown>): { value?: unknown; diagnostics: RuntimeDiagnostic[] } {
  if (!source.trim()) return { diagnostics: [{ severity: 'error', code: 'empty', message: 'Enter a theme(ctx) recipe.' }] }
  if (source.length > MAX_SOURCE_LENGTH) {
    return { diagnostics: [{ severity: 'error', code: 'complexity', message: `Recipe exceeds ${MAX_SOURCE_LENGTH.toLocaleString()} characters.` }] }
  }

  let program: any
  try {
    program = parse(source, { ecmaVersion: 'latest', locations: true, sourceType: 'script' })
  } catch (error: any) {
    return {
      diagnostics: [{
        severity: 'error',
        code: 'syntax',
        message: error.message || 'Invalid JavaScript syntax.',
        line: error.loc?.line,
        column: error.loc?.column !== undefined ? error.loc.column + 1 : undefined,
      }],
    }
  }

  let steps = 0
  let callDepth = 0
  const tick = (node?: any) => {
    steps += 1
    if (steps > MAX_STEPS) throw new RuntimeFailure('complexity', `Recipe exceeded the ${MAX_STEPS.toLocaleString()}-step execution limit.`, node)
  }

  const propertyName = (node: any, env: Environment): string | number => {
    const key = node.computed ? evaluate(node.property, env) : node.property.name
    if (typeof key !== 'string' && typeof key !== 'number') throw new RuntimeFailure('property', 'Property keys must be strings or numbers.', node)
    if (FORBIDDEN_PROPERTIES.has(String(key))) throw new RuntimeFailure('security', `Access to “${String(key)}” is not allowed.`, node)
    return key
  }

  const readMember = (object: any, key: string | number, node: any): unknown => {
    if (object === null || object === undefined) throw new RuntimeFailure('property', `Cannot read “${String(key)}” from ${object}.`, node)
    if (FORBIDDEN_PROPERTIES.has(String(key))) throw new RuntimeFailure('security', `Access to “${String(key)}” is not allowed.`, node)
    const value = object[key]
    if (typeof value !== 'function') return value
    if (Array.isArray(object) && ARRAY_METHODS.has(String(key))) return value.bind(object)
    if (typeof object === 'string' && STRING_METHODS.has(String(key))) return value.bind(object)
    if (Object.isFrozen(object) && Object.prototype.hasOwnProperty.call(object, key)) return value
    throw new RuntimeFailure('security', `Calling method “${String(key)}” is not allowed.`, node)
  }

  const assignPattern = (pattern: any, value: any, env: Environment, declare = true) => {
    if (pattern.type === 'Identifier') {
      declare ? env.declare(pattern.name, value) : env.set(pattern.name, value, pattern)
      return
    }
    if (pattern.type === 'AssignmentPattern') {
      assignPattern(pattern.left, value === undefined ? evaluate(pattern.right, env) : value, env, declare)
      return
    }
    if (pattern.type === 'ObjectPattern') {
      for (const property of pattern.properties) {
        if (property.type === 'RestElement') throw new RuntimeFailure('unsupported', 'Rest destructuring is not supported.', property)
        const key = property.computed ? evaluate(property.key, env) : property.key.name ?? property.key.value
        assignPattern(property.value, value?.[key], env, declare)
      }
      return
    }
    if (pattern.type === 'ArrayPattern') {
      pattern.elements.forEach((element: any, index: number) => element && assignPattern(element, value?.[index], env, declare))
      return
    }
    throw new RuntimeFailure('unsupported', `Unsupported assignment pattern ${pattern.type}.`, pattern)
  }

  const createFunction = (node: any, closure: Environment) => (...args: unknown[]) => {
    tick(node)
    callDepth += 1
    if (callDepth > MAX_CALL_DEPTH) throw new RuntimeFailure('complexity', `Recipe exceeded the ${MAX_CALL_DEPTH}-call recursion limit.`, node)
    const local = new Environment(closure)
    node.params.forEach((parameter: any, index: number) => assignPattern(parameter, args[index], local))
    try {
      if (node.body.type === 'BlockStatement') {
        executeBlock(node.body.body, local)
        return undefined
      }
      return evaluate(node.body, local)
    } catch (signal) {
      if (signal instanceof ReturnSignal) return signal.value
      throw signal
    } finally {
      callDepth -= 1
    }
  }

  const evaluate = (node: any, env: Environment): any => {
    tick(node)
    switch (node.type) {
      case 'Literal': return node.value
      case 'Identifier': return env.get(node.name, node)
      case 'ArrayExpression': return node.elements.map((item: any) => item ? evaluate(item, env) : undefined)
      case 'ObjectExpression': {
        const object = Object.create(null)
        for (const property of node.properties) {
          if (property.type === 'SpreadElement') {
            const spread = evaluate(property.argument, env)
            if (!spread || typeof spread !== 'object') throw new RuntimeFailure('type', 'Only objects can be spread into an object.', property)
            Object.assign(object, spread)
          } else {
            const key = property.computed ? evaluate(property.key, env) : property.key.name ?? property.key.value
            if (FORBIDDEN_PROPERTIES.has(String(key))) throw new RuntimeFailure('security', `Property “${String(key)}” is not allowed.`, property)
            object[key] = property.shorthand ? env.get(property.key.name, property) : evaluate(property.value, env)
          }
        }
        return object
      }
      case 'UnaryExpression': {
        const value = evaluate(node.argument, env)
        if (node.operator === '+') return +value
        if (node.operator === '-') return -value
        if (node.operator === '!') return !value
        if (node.operator === 'typeof') return typeof value
        throw new RuntimeFailure('unsupported', `Unary operator “${node.operator}” is not supported.`, node)
      }
      case 'BinaryExpression': {
        const left = evaluate(node.left, env)
        const right = evaluate(node.right, env)
        switch (node.operator) {
          case '+': return left + right
          case '-': return left - right
          case '*': return left * right
          case '/': return left / right
          case '%': return left % right
          case '**': return left ** right
          case '<': return left < right
          case '<=': return left <= right
          case '>': return left > right
          case '>=': return left >= right
          case '==': return left == right // Deliberately follows JavaScript recipe semantics.
          case '!=': return left != right
          case '===': return left === right
          case '!==': return left !== right
          default: throw new RuntimeFailure('unsupported', `Binary operator “${node.operator}” is not supported.`, node)
        }
      }
      case 'LogicalExpression': {
        const left = evaluate(node.left, env)
        if (node.operator === '&&') return left && evaluate(node.right, env)
        if (node.operator === '||') return left || evaluate(node.right, env)
        if (node.operator === '??') return left ?? evaluate(node.right, env)
        throw new RuntimeFailure('unsupported', `Logical operator “${node.operator}” is not supported.`, node)
      }
      case 'ConditionalExpression': return evaluate(node.test, env) ? evaluate(node.consequent, env) : evaluate(node.alternate, env)
      case 'MemberExpression': {
        const object = evaluate(node.object, env)
        return readMember(object, propertyName(node, env), node)
      }
      case 'CallExpression': {
        const fn = evaluate(node.callee, env)
        if (typeof fn !== 'function') throw new RuntimeFailure('type', 'Attempted to call a value that is not a function.', node)
        try {
          return fn(...node.arguments.map((argument: any) => {
            if (argument.type === 'SpreadElement') return evaluate(argument.argument, env)
            return evaluate(argument, env)
          }).flatMap((value: any, index: number) => node.arguments[index]?.type === 'SpreadElement' ? Array.from(value) : [value]))
        } catch (error) {
          if (error instanceof RuntimeFailure || error instanceof ReturnSignal) throw error
          throw new RuntimeFailure('runtime', error instanceof Error ? error.message : 'Function call failed.', node)
        }
      }
      case 'ArrowFunctionExpression':
      case 'FunctionExpression': return createFunction(node, env)
      case 'AssignmentExpression': {
        const right = evaluate(node.right, env)
        const current = node.operator === '=' ? undefined : evaluate(node.left, env)
        const next = node.operator === '=' ? right
          : node.operator === '+=' ? current + right
          : node.operator === '-=' ? current - right
          : node.operator === '*=' ? current * right
          : node.operator === '/=' ? current / right
          : node.operator === '%=' ? current % right
          : undefined
        if (next === undefined && node.operator !== '=') throw new RuntimeFailure('unsupported', `Assignment operator “${node.operator}” is not supported.`, node)
        if (node.left.type === 'Identifier') return env.set(node.left.name, next, node.left)
        if (node.left.type === 'MemberExpression') {
          const object = evaluate(node.left.object, env)
          const key = propertyName(node.left, env)
          object[key] = next
          return next
        }
        throw new RuntimeFailure('unsupported', 'Unsupported assignment target.', node)
      }
      case 'UpdateExpression': {
        const oldValue = Number(evaluate(node.argument, env))
        const next = node.operator === '++' ? oldValue + 1 : oldValue - 1
        if (node.argument.type === 'Identifier') env.set(node.argument.name, next, node.argument)
        else throw new RuntimeFailure('unsupported', 'Update operators are only supported on local variables.', node)
        return node.prefix ? next : oldValue
      }
      case 'TemplateLiteral': return node.quasis.reduce((text: string, quasi: any, index: number) => text + quasi.value.cooked + (node.expressions[index] ? String(evaluate(node.expressions[index], env)) : ''), '')
      default: throw new RuntimeFailure('unsupported', `Expression ${node.type} is not supported.`, node)
    }
  }

  const executeStatement = (node: any, env: Environment) => {
    tick(node)
    switch (node.type) {
      case 'EmptyStatement': return
      case 'ExpressionStatement': evaluate(node.expression, env); return
      case 'VariableDeclaration':
        node.declarations.forEach((declaration: any) => assignPattern(declaration.id, declaration.init ? evaluate(declaration.init, env) : undefined, env))
        return
      case 'FunctionDeclaration': env.declare(node.id.name, createFunction(node, env)); return
      case 'ReturnStatement': throw new ReturnSignal(node.argument ? evaluate(node.argument, env) : undefined)
      case 'BlockStatement': executeBlock(node.body, new Environment(env)); return
      case 'IfStatement':
        if (evaluate(node.test, env)) executeStatement(node.consequent, env)
        else if (node.alternate) executeStatement(node.alternate, env)
        return
      case 'ForStatement': {
        const loopEnv = new Environment(env)
        if (node.init) node.init.type === 'VariableDeclaration' ? executeStatement(node.init, loopEnv) : evaluate(node.init, loopEnv)
        while (!node.test || evaluate(node.test, loopEnv)) {
          executeStatement(node.body, loopEnv)
          if (node.update) evaluate(node.update, loopEnv)
        }
        return
      }
      case 'ForOfStatement': {
        const iterable = evaluate(node.right, env)
        for (const value of iterable) {
          const loopEnv = new Environment(env)
          if (node.left.type === 'VariableDeclaration') assignPattern(node.left.declarations[0].id, value, loopEnv)
          else assignPattern(node.left, value, loopEnv, false)
          executeStatement(node.body, loopEnv)
        }
        return
      }
      case 'WhileStatement':
        while (evaluate(node.test, env)) executeStatement(node.body, env)
        return
      default: throw new RuntimeFailure('unsupported', `Statement ${node.type} is not supported.`, node)
    }
  }

  const executeBlock = (statements: any[], env: Environment) => {
    for (const statement of statements) {
      if (statement.type === 'FunctionDeclaration') env.declare(statement.id.name, createFunction(statement, env))
    }
    for (const statement of statements) {
      if (statement.type !== 'FunctionDeclaration') executeStatement(statement, env)
    }
  }

  try {
    const root = new Environment()
    root.declare('Math', safeMath())
    root.declare('Array', safeArray())
    root.declare('Infinity', Infinity)
    root.declare('NaN', NaN)
    root.declare('undefined', undefined)
    for (const [name, value] of Object.entries(globals)) root.declare(name, value)
    executeBlock(program.body, root)
    const theme = root.get('theme', program)
    if (typeof theme !== 'function') throw new RuntimeFailure('contract', 'Recipe must declare function theme(ctx).', program)
    return { value: theme(globals.ctx), diagnostics: [] }
  } catch (error: any) {
    if (error instanceof RuntimeFailure) {
      return { diagnostics: [{ severity: 'error', code: error.code, message: error.message, line: error.line, column: error.column }] }
    }
    return { diagnostics: [{ severity: 'error', code: 'runtime', message: error?.message || 'Recipe execution failed.' }] }
  }
}
